import { useEffect, useState } from 'react';
import { MoveVertical, X } from 'lucide-react';

interface MoveSongOption {
  id: string;
  title: string;
  artist: string;
}

type PlaceBeforeFilter = 'all' | 'ranked' | 'unranked';

interface MoveSongModalProps {
  song: MoveSongOption;
  isNew: boolean;
  order: MoveSongOption[];
  rankSize: number;
  dividerIsBelowThisSong: boolean;
  onMoveToPosition: (position: number) => void;
  onPlaceBefore: (beforeId: string) => void;
  onMarkSortedHere: () => void;
  onRemoveSortedLine: () => void;
  onClose: () => void;
}

const FILTER_LABELS: Record<PlaceBeforeFilter, string> = { all: 'All', ranked: 'Ranked', unranked: 'Unranked' };

export function MoveSongModal({
  song, isNew, order, rankSize, dividerIsBelowThisSong,
  onMoveToPosition, onPlaceBefore, onMarkSortedHere, onRemoveSortedLine, onClose,
}: MoveSongModalProps) {
  const [positionText, setPositionText] = useState('');
  const [filter, setFilter] = useState<PlaceBeforeFilter>('all');

  const total = order.length + (isNew ? 1 : 0);
  const position = Number(positionText);
  const positionValid = Number.isInteger(position) && position >= 1 && position <= total;
  const showRangeError = positionText !== '' && !positionValid;

  const numbered = order.map((option, index) => ({ ...option, position: index + 1 }));
  const choices = numbered
    .filter(option => option.id !== song.id)
    .filter(option => filter === 'all'
      || (filter === 'ranked' ? option.position <= rankSize : option.position > rankSize));

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="move-song-modal"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-xl max-w-md w-full max-h-[90vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-song-title"
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <MoveVertical className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 id="move-song-title" className="text-base font-semibold text-gray-900 dark:text-white truncate">
                Move &ldquo;{song.title}&rdquo;
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{song.artist}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          className="p-4 border-b border-gray-200 dark:border-gray-700"
          onSubmit={event => {
            event.preventDefault();
            if (positionValid) onMoveToPosition(position);
          }}
        >
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="move-song-position">
            Move to number
          </label>
          <div className="flex items-start gap-3 mt-1">
            <div className="flex-1">
              <input
                id="move-song-position"
                type="number"
                inputMode="numeric"
                min={1}
                max={total}
                value={positionText}
                onChange={event => setPositionText(event.target.value)}
                className="input w-full"
                aria-invalid={showRangeError}
              />
              {showRangeError ? (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">Pick a number from 1 to {total}</p>
              ) : (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">#1–#{rankSize} count toward the queue</p>
              )}
            </div>
            <button type="submit" disabled={!positionValid} className="btn btn-primary disabled:opacity-50 whitespace-nowrap">
              {positionValid ? `Move to #${position}` : 'Move'}
            </button>
          </div>
        </form>

        <div className="px-4 pt-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Or place before</p>
          <div className="grid grid-cols-3 gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg" role="group" aria-label="Show songs">
            {(Object.keys(FILTER_LABELS) as PlaceBeforeFilter[]).map(option => (
              <button
                key={option}
                type="button"
                onClick={() => setFilter(option)}
                aria-pressed={filter === option}
                className={`py-1 text-xs font-medium rounded-md transition-colors ${
                  filter === option
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {FILTER_LABELS[option]}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-2 min-h-[8rem]">
          {choices.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No songs here</p>
          )}
          {choices.map((option, index) => {
            const crossesIntoUnranked = filter === 'all' && option.position > rankSize
              && (index === 0 || choices[index - 1].position <= rankSize);
            return (
              <div key={option.id}>
                {crossesIntoUnranked && index > 0 && (
                  <div className="flex items-center gap-2 my-1 text-[10px] uppercase tracking-wide text-gray-400">
                    <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                    Unranked
                    <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onPlaceBefore(option.id)}
                  className="w-full text-left p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
                >
                  <span className={`min-w-[2rem] text-center text-sm font-bold ${option.position <= rankSize ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>
                    #{option.position}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">{option.title}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{option.artist}</span>
                  </span>
                </button>
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          {dividerIsBelowThisSong ? (
            <div className="flex items-center justify-between text-sm">
              <span className="text-teal-600 dark:text-teal-400">The &ldquo;sorted up to here&rdquo; line is below this song</span>
              <button type="button" onClick={onRemoveSortedLine} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline">
                Remove line
              </button>
            </div>
          ) : !isNew && (
            <button type="button" onClick={onMarkSortedHere} className="btn btn-secondary w-full text-sm">
              Mark &ldquo;sorted up to here&rdquo; below this song
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
